// Ficha de una persona: todo lo del cliente en una pantalla y el boton de llamar siempre a mano.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import * as crm from '../api/crm';
import type { Llamada, Persona } from '../api/types';
import { useAgentes, useEtapas, useLlamadasPersona, usePersona } from '../data/queries';
import { escribiendo, useKey } from '../hooks';
import { ago, dueLabel, duration, money } from '../lib/format';
import { Icon } from '../lib/icons';
import { formatPhone } from '../lib/phone-number';
import { phone } from '../phone/store';
import { Avatar, Empty, ErrorBox, IconDot, Pill, Skeleton, Soon, Tel } from '../ui/bits';
import { abrirDialogo } from '../ui/forms';
import { NotaItem } from '../ui/Nota';
import { textoPlano } from '../ui/Texto';
import { toast } from '../ui/toasts';

type Filtro = 'todo' | 'llamadas' | 'notas' | 'tareas' | 'correos';

export function PersonaFicha() {
  const { id = '' } = useParams();
  const p = usePersona(id);
  const nav = useNavigate();

  useKey((e) => {
    if (escribiendo(e) || e.metaKey || e.ctrlKey || document.querySelector('.dialog,.cmdk')) return;
    if (e.key.toLowerCase() === 'n' && p.data?.telefono && !phone.getState().call) {
      e.preventDefault();
      llamar(p.data);
    }
  });

  if (p.isLoading)
    return (
      <div className="view">
        <Skeleton rows={6} height={22} />
      </div>
    );
  if (p.error || !p.data)
    return (
      <div className="view">
        <ErrorBox error={p.error ?? new Error('No encontramos este contacto.')} onRetry={() => p.refetch()} />
        <button className="btn" onClick={() => nav('/contactos')}>
          Volver a contactos
        </button>
      </div>
    );
  return <Ficha persona={p.data} />;
}

const llamar = (x: Persona) => phone.dial(x.telefono, { id: x.id, nombre: x.nombreCompleto, empresa: x.empresa?.nombre });

function Ficha({ persona: x }: { persona: Persona }) {
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<Filtro>('todo');
  const [nota, setNota] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const llamadas = useLlamadasPersona(x.id);
  const notas = useQuery({ queryKey: ['timeline', 'notas', x.id], queryFn: () => crm.notasDe({ personaId: x.id }) });
  const tareas = useQuery({ queryKey: ['timeline', 'tareas', x.id], queryFn: () => crm.tareasDePersona(x.id) });
  const correos = useQuery({ queryKey: ['timeline', 'correos', x.id], queryFn: () => crm.correosDePersona(x.id) });
  const colegas = useQuery({ queryKey: ['colegas', x.empresaId], queryFn: () => crm.personasDeEmpresa(x.empresaId!), enabled: !!x.empresaId });
  // Las oportunidades donde esta persona es el contacto.
  const opps = useQuery({ queryKey: ['oportunidades', 'persona', x.id], queryFn: () => crm.oportunidadesDePersona(x.id) });
  const etapas = useEtapas();
  const ultima = llamadas.data?.[0];

  const guardarNota = async () => {
    if (!nota?.trim()) return;
    setGuardando(true);
    try {
      await crm.crearNota({ texto: nota, personaId: x.id });
      setNota(null);
      void qc.invalidateQueries({ queryKey: ['timeline', 'notas', x.id] });
      toast('Nota guardada.');
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setGuardando(false);
    }
  };

  const toggleTarea = async (id: string, hecha: boolean) => {
    try {
      await crm.cambiarEstadoTarea(id, hecha ? 'TODO' : 'DONE');
      void qc.invalidateQueries({ queryKey: ['timeline', 'tareas', x.id] });
      void qc.invalidateQueries({ queryKey: ['tareas'] });
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };

  type Item = { k: Filtro; t: number; node: React.ReactNode; key: string };
  const items: Item[] = useMemo(() => {
    const out: Item[] = [];
    for (const l of llamadas.data ?? []) out.push({ k: 'llamadas', t: new Date(l.inicio ?? l.creado).getTime(), key: `l${l.id}`, node: <ItemLlamada l={l} persona={x} /> });
    for (const n of notas.data ?? [])
      out.push({
        k: 'notas',
        t: new Date(n.creado).getTime(),
        key: `n${n.id}`,
        node: <NotaItem n={n} onCambio={() => void qc.invalidateQueries({ queryKey: ['timeline', 'notas', x.id] })} />,
      });
    for (const t of tareas.data ?? []) {
      const hecha = t.estado === 'DONE';
      out.push({
        k: 'tareas',
        t: new Date(t.creado).getTime(),
        key: `t${t.id}`,
        node: (
          <article className={`tl-item ${hecha ? 'done' : ''}`}>
            <IconDot name="task" tone={hecha ? 'ok' : 'warn'} />
            <div style={{ minWidth: 0 }}>
              <div className="tl-top">
                <b>Tarea</b>
                <Pill tone={hecha ? 'ok' : 'warn'}>{hecha ? 'Hecha' : dueLabel(t.vence)}</Pill>
                <button className="btn sm ghost" onClick={() => toggleTarea(t.id, hecha)}>
                  {hecha ? 'Reabrir' : 'Marcar hecha'}
                </button>
                <time>{ago(t.creado)}</time>
              </div>
              <button className="tarea-abrir tl-body" onClick={() => abrirDialogo({ tipo: 'verTarea', id: t.id })} aria-label={`Abrir tarea: ${t.titulo}`}>
                <span className="nm tarea-titulo">{t.titulo}</span>
                {t.cuerpo ? <span className="sb tarea-resumen">{textoPlano(t.cuerpo, 140)}</span> : null}
              </button>
            </div>
          </article>
        ),
      });
    }
    for (const c of correos.data ?? [])
      out.push({
        k: 'correos',
        t: new Date(c.fecha).getTime(),
        key: `c${c.id}`,
        node: (
          <article className="tl-item">
            <IconDot name="mail" />
            <div style={{ minWidth: 0 }}>
              <div className="tl-top">
                <b>{c.direccion === 'saliente' ? 'Correo enviado' : 'Correo recibido'}</b>
                <time>{ago(c.fecha)}</time>
              </div>
              <div className="tl-body">
                <b style={{ color: 'var(--text)', fontWeight: 500 }}>{c.asunto}</b>
                <br />
                {c.texto}
              </div>
            </div>
          </article>
        ),
      });
    return out.sort((a, b) => b.t - a.t);
  }, [llamadas.data, notas.data, tareas.data, correos.data, x]);

  const visibles = items.filter((i) => filtro === 'todo' || i.k === filtro);
  const cargando = llamadas.isLoading || notas.isLoading || tareas.isLoading;
  const otros = (colegas.data ?? []).filter((c) => c.id !== x.id);

  return (
    <div className="view">
      <div className="crumbs">
        <Link to="/contactos">Contactos</Link>
        <Icon name="chevRight" />
        <span>{x.nombreCompleto}</span>
      </div>
      <div className="fhead">
        <Avatar nombre={x.nombreCompleto} size="lg" />
        <div className="who">
          <h1>{x.nombreCompleto}</h1>
          <div className="rl">
            {x.cargo ? <span>{x.cargo}</span> : null}
            {x.empresa ? (
              <>
                {x.cargo ? <span>en</span> : null}
                <Link to={`/empresas/${x.empresa.id}`}>{x.empresa.nombre}</Link>
              </>
            ) : null}
            {!x.cargo && !x.empresa ? <span>Sin empresa</span> : null}
            {x.email ? (
              <button
                className="linkish"
                title="Las campañas de Marketing se saltan a este contacto"
                onClick={() =>
                  void crm
                    .editarCampanasPersona(x.id, !x.noCampanas)
                    .then(() => (qc.invalidateQueries({ queryKey: ['persona', x.id] }), toast(x.noCampanas ? 'Volverá a recibir campañas.' : 'No recibirá más campañas.')))
                    .catch((e: Error) => toast(e.message, 'error'))
                }
              >
                {x.noCampanas ? 'No recibe campañas · reactivar' : 'Recibe campañas'}
              </button>
            ) : null}
          </div>
        </div>
        <div className="vh-act">
          <button className="btn primary lg" onClick={() => llamar(x)} disabled={!x.telefono} title={x.telefono ? `Llamar a ${formatPhone(x.telefono)}` : 'Sin teléfono'} data-testid="llamar-ficha">
            <Icon name="phone" />
            Llamar
            <kbd>N</kbd>
          </button>
          <button className="btn lg" onClick={() => abrirDialogo({ tipo: 'cita', personaId: x.id, personaNombre: x.nombreCompleto, empresaId: x.empresa?.id })}>
            <Icon name="calendar" />
            Cita
          </button>
          {x.email ? (
            <button className="btn lg" onClick={() => abrirDialogo({ tipo: 'correo', para: x.email })}>
              <Icon name="mail" />
              Correo
            </button>
          ) : null}
          <button className="btn lg" onClick={() => setNota((v) => (v === null ? '' : null))}>
            <Icon name="note" />
            Nota
          </button>
          <button className="btn lg" onClick={() => abrirDialogo({ tipo: 'tarea', personaId: x.id, nombre: x.nombreCompleto })}>
            <Icon name="task" />
            Tarea
          </button>
          <button className="btn lg icon" style={{ width: 40 }} onClick={() => abrirDialogo({ tipo: 'persona', persona: x })} aria-label="Editar contacto" title="Editar">
            <Icon name="edit" />
          </button>
        </div>
      </div>
      <div className="fgrid">
        <aside className="box">
          <dl className="dl">
            <div>
              <dt>Teléfono</dt>
              <dd>{x.telefono ? <Tel numero={x.telefono} contacto={{ id: x.id, nombre: x.nombreCompleto, empresa: x.empresa?.nombre }} /> : '—'}</dd>
            </div>
            <div>
              <dt>Correo</dt>
              <dd>{x.email ? <button type="button" className="link" onClick={() => abrirDialogo({ tipo: 'correo', para: x.email })}>{x.email}</button> : '—'}</dd>
            </div>
            <div>
              <dt>Empresa</dt>
              <dd>{x.empresa ? <Link to={`/empresas/${x.empresa.id}`}>{x.empresa.nombre}</Link> : '—'}</dd>
            </div>
            <div>
              <dt>Último contacto</dt>
              <dd>{ultima ? `${ago(ultima.inicio)} · ${ultima.direccion === 'ENTRANTE' ? 'entrante' : 'saliente'}` : '—'}</dd>
            </div>
            <div>
              <dt>En el CRM desde</dt>
              <dd>{new Date(x.creado).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })}</dd>
            </div>
          </dl>
          <div className="box-h" style={{ marginTop: 6 }}>
            <p className="lbl" style={{ margin: 0 }}>
              Oportunidades
            </p>
            <button className="btn sm ghost" onClick={() => abrirDialogo({ tipo: 'oportunidad', empresaId: x.empresaId ?? undefined, contactoId: x.id })}>
              <Icon name="plus" /> Nueva
            </button>
          </div>
          {opps.data?.length ? (
            opps.data.map((o) => (
              <button key={o.id} className="tarea-abrir" onClick={() => abrirDialogo({ tipo: 'verOportunidad', id: o.id })} aria-label={`Abrir oportunidad: ${o.nombre}`}>
                <div style={{ fontWeight: 500 }}>{o.nombre}</div>
                <div className="sb" style={{ color: 'var(--muted)', fontSize: 12.5 }}>
                  {etapas.data?.find((e) => e.valor === o.etapa)?.etiqueta ?? o.etapa} · <span className="mono">{money(o.monto, o.moneda)}</span>
                </div>
              </button>
            ))
          ) : (
            <p className="sb" style={{ margin: 0 }}>
              Sin oportunidades con este contacto.
            </p>
          )}
          {otros.length ? (
            <>
              <p className="lbl" style={{ marginTop: 6 }}>
                Otros en {x.empresa?.nombre}
              </p>
              <div className="rows">
                {otros.slice(0, 6).map((o) => (
                  <Link key={o.id} className="row act-row" to={`/personas/${o.id}`}>
                    <Avatar nombre={o.nombreCompleto} />
                    <div style={{ minWidth: 0 }}>
                      <div className="nm">{o.nombreCompleto}</div>
                      <div className="sb">{o.cargo || '—'}</div>
                    </div>
                    <span />
                  </Link>
                ))}
              </div>
            </>
          ) : null}
        </aside>
        <section style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
          <div className="chips" role="group" aria-label="Filtrar actividad">
            {(
              [
                ['todo', 'Todo'],
                ['llamadas', `Llamadas${llamadas.data?.length ? ` · ${llamadas.data.length}` : ''}`],
                ['notas', 'Notas'],
                ['tareas', 'Tareas'],
                ['correos', 'Correos'],
              ] as [Filtro, string][]
            ).map(([id, n]) => (
              <button key={id} className="chip" aria-pressed={filtro === id} onClick={() => setFiltro(id)}>
                {n}
              </button>
            ))}
          </div>
          {nota !== null ? (
            <div className="composer box">
              <label className="lbl" htmlFor="note-in">
                Nota rápida
              </label>
              <textarea id="note-in" value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Escribe lo importante de este cliente…" autoFocus />
              <div className="vh-act">
                <button className="btn primary" onClick={guardarNota} disabled={!nota.trim() || guardando}>
                  {guardando ? 'Guardando…' : 'Guardar nota'}
                </button>
                <button className="btn ghost" onClick={() => setNota(null)}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : null}
          {(filtro === 'todo' || filtro === 'llamadas') && llamadas.data?.some((l) => l.duracion > 0) ? (
            <Soon titulo="Grabación y resumen con IA de cada llamada" icon="spark">
              Llegan con la integración XAPI de 3CX (fase 3): escucharás la grabación aquí mismo y leerás un resumen de tres líneas.
            </Soon>
          ) : null}
          {filtro === 'correos' && !correos.data?.length ? (
            <Soon titulo="Sin correos para mostrar" icon="mail">
              Si conectas tu correo en Twenty (Ajustes → Cuentas), los mensajes con este contacto aparecen aquí.
            </Soon>
          ) : null}
          {cargando ? (
            <Skeleton rows={5} />
          ) : visibles.length ? (
            <div className="tl">
              {visibles.map((i) => (
                <div key={i.key}>{i.node}</div>
              ))}
            </div>
          ) : filtro !== 'correos' ? (
            <Empty titulo="Aún no hay nada aquí" texto="Las llamadas, notas y tareas con este contacto aparecerán en orden." />
          ) : null}
        </section>
      </div>
    </div>
  );
}

function ItemLlamada({ l, persona }: { l: Llamada; persona: Persona }) {
  const perdida = l.estado !== 'CONTESTADA';
  const titulo = perdida ? (l.direccion === 'ENTRANTE' ? 'Llamada perdida' : 'No contestada') : l.direccion === 'ENTRANTE' ? 'Llamada entrante' : 'Llamada saliente';
  const agentes = useAgentes();
  return (
    <article className="tl-item">
      <IconDot name={perdida ? 'phoneMiss' : l.direccion === 'ENTRANTE' ? 'phoneIn' : 'phoneOut'} tone={perdida ? 'bad' : 'acc'} />
      <div style={{ minWidth: 0 }}>
        <div className="tl-top">
          <button type="button" className="link" onClick={() => abrirDialogo({ tipo: 'verLlamada', id: l.id })} title="Ver el detalle de la llamada">
            <b>{titulo}</b>
          </button>
          {l.duracion ? <span className="mono" style={{ color: 'var(--muted)' }}>{duration(l.duracion)}</span> : null}
          {l.tipificacion ? (
            <Pill tone={l.gestion === 'SEGUIMIENTO' ? 'warn' : 'acc'}>{l.tipificacion.nombre}</Pill>
          ) : l.duracion > 0 ? (
            <button
              className="btn sm"
              onClick={() =>
                phone.abrirCierreExistente({
                  llamadaId: l.id,
                  numero: l.numero,
                  contacto: { id: persona.id, nombre: persona.nombreCompleto, empresa: persona.empresa?.nombre },
                  dir: l.direccion === 'ENTRANTE' ? 'in' : 'out',
                  duracion: l.duracion,
                  inicio: new Date(l.inicio ?? l.creado).getTime(),
                  notas: l.notas,
                })
              }
            >
              Tipificar
            </button>
          ) : null}
          {l.agente ? <span className="sb">{agentes.nombre(l.agente)}</span> : null}
          <time title={new Date(l.inicio ?? l.creado).toLocaleString('es-CO')}>{ago(l.inicio)}</time>
        </div>
        {l.notas ? <div className="tl-body">{l.notas}</div> : null}
      </div>
    </article>
  );
}
